import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Spinner from '../ogComponent/spinner';

export default function Report() {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const scrollContainerRef = useRef(null);

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);

    const pagePermissions = ["showattendancereportID", "downloadattendancereportID"];

    // Check if the screen is mobile size
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768); // Bootstrap's medium breakpoint
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Helper function to format date as YYYY-MM-DD
    const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to format date as DD/MM/YYYY for display
    const formatDateForDisplay = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Get all days in the selected month
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        for (let i = 1; i <= daysInMonth; i++) {
            const currentDate = new Date(year, month, i);
            days.push(currentDate);
        }
        return days;
    };

    // Fetch attendance data
    const fetchAttendanceData = async (date) => {
        setLoading(true);
        setError(null);

        try {
            const apiDate = formatDateForAPI(new Date(date.getFullYear(), date.getMonth(), 1));
            const token = localStorage.getItem('accessToken');

            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/attendance/attendance-report/?date=${apiDate}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setAttendanceData(response.data.data);
            } else {
                setError('Failed to fetch attendance report');
            }
        } catch (err) {
            setError('Error connecting to server');
            console.error('Error fetching attendance report:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (permissions.includes("showattendancereportID")) {
            fetchAttendanceData(selectedDate);
        } else {
            setLoading(false);
        }
    }, [selectedDate, permissions]);

    // Handle month input change
    const handleDateChange = (e) => {
        const [year, month] = e.target.value.split('-');
        setSelectedDate(new Date(year, month - 1, 1));
    };

    // Get current month in YYYY-MM format for month input
    const getCurrentMonthString = () => {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    // Find attendance entry for a specific employee and date
    const findAttendanceForDate = (employeeAttendance, dateString) => {
        return employeeAttendance.find(entry => entry.date === dateString) || null;
    };

    // Render status cell with appropriate styling
    const renderStatusCell = (attendanceEntry) => {
        if (!attendanceEntry) return <td className="text-center">-</td>;

        let statusClass = "text-center ";
        let content = "";

        if (attendanceEntry.status === "Present") {
            statusClass += "bg-success bg-opacity-10";
            content = attendanceEntry.first_punch || "-";
            if (attendanceEntry.last_punch) {
                content += " - " + attendanceEntry.last_punch;
            }
            if (attendanceEntry.leave_type) {
                content += ` (${attendanceEntry.leave_type} -`;
            }
            if (attendanceEntry.total_hours) {
                content += ` ${attendanceEntry.total_hours})`;
            }
        } else if (attendanceEntry.status === "Absent") {
            statusClass += "bg-danger bg-opacity-10 fw-semibold";
            content = "Absent";
        } else if (attendanceEntry.status === "Weekend" || attendanceEntry.status === "Holiday") {
            statusClass += "bg-light";
            content = attendanceEntry.status;
        } else {
            content = attendanceEntry.status;
        }

        return <td className={statusClass}>{content}</td>;
    };

    // Handle download button click
    const handleDownload = async () => {
        if (!permissions.includes("downloadattendancereportID")) return;

        const apiDate = formatDateForAPI(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
        const token = localStorage.getItem('accessToken');

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/attendance/download-attendance-report/?date=${apiDate}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    responseType: 'blob'
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `attendance_report_${apiDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading attendance report:', err);
            setError('Error downloading report');
        }
    };

    const daysInMonth = getDaysInMonth(selectedDate);

    return (
        <div className="p-3 px-4 overflow-x-auto">
            <h3 className="mb-4">Attendance Report</h3>

            {permissions.includes("showattendancereportID") && (
                <div className="mb-4 w-100 d-flex justify-content-between">
                    <input
                        type="month"
                        value={getCurrentMonthString()}
                        onChange={handleDateChange}
                        className="form-control w-auto"
                    />
                    {permissions.includes("downloadattendancereportID") && (
                        <button
                            className='btn btn-success'
                            style={{ fontSize: "clamp(12px, 1.4vw, 16px)" }}
                            onClick={handleDownload}
                        >
                            Download File
                        </button>
                    )}
                </div>
            )}

            {loading && <div style={{width:'75vw'}}><Spinner /></div>}
            {error && <div className="mb-4 text-danger">{error}</div>}

            {permissions.includes("showattendancereportID") ? (
                !loading && !error && attendanceData.length > 0 ? (
                    <div className="position-relative" style={{ zIndex: '1' }}>
                        <div
                            className="overflow-auto"
                            style={{ maxHeight: "66vh" }}
                            ref={scrollContainerRef}
                        >
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th
                                            className={`text-white ${!isMobile ? "position-sticky start-0" : ""}`}
                                            style={{
                                                width: "12rem",
                                                zIndex: 20,
                                                backgroundColor: '#120e4d',
                                                ...(isMobile ? {} : { position: 'sticky', left: 0 })
                                            }}
                                        >
                                            Name
                                        </th>
                                        {daysInMonth.map((day, index) => (
                                            <th
                                                key={index}
                                                className="text-center text-white position-sticky top-0 px-5"
                                                style={{ width: "8rem", backgroundColor: '#120e4d' }}
                                            >
                                                {formatDateForDisplay(formatDateForAPI(day))}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {attendanceData.map((employee, empIndex) => (
                                        <tr key={empIndex}>
                                            <td
                                                className={`${!isMobile ? "position-sticky start-0" : ""} bg-white`}
                                                style={{
                                                    width: "12rem",
                                                    zIndex: 10,
                                                    ...(isMobile ? {} : { position: 'sticky', left: 0 })
                                                }}
                                            >
                                                {employee.employee_name}
                                                <div className="small text-muted">Leaves: {employee.total_unpaid_leaves}</div>
                                                <div className="small text-muted">Approved Leaves: {employee.approved_leave_count}</div>
                                            </td>
                                            {daysInMonth.map((day, dayIndex) => {
                                                const dateStr = formatDateForAPI(day);
                                                const attendance = findAttendanceForDate(employee.attendance, dateStr);
                                                return renderStatusCell(attendance);
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : !loading && !error && attendanceData.length === 0 ? (
                    <div className="p-4 text-center">No attendance data available</div>
                ) : null
            ) : (
                <p>No permission to view attendance report.</p>
            )}
        </div>
    );
}